import animation from '@helpers/animation';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import global from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import ChevronUrl from '@img/icons/chevron.svg?url';
import XWhite from '@img/icons/x-white.svg?url';
import { ButtonStyles } from '@stories/Elements/Forms/Buttons/Button.styles';
import { FilterDropdownStyles } from '@stories/Elements/ProductFilters/FilterDropdown/FilterDropdown.styles';
import { Typography } from '@stories/Elements/Typography/Typography.styles';
import { Grid, defaultPadding } from '@stories/Settings/Grids/Grid.styles';
import styled, { css } from 'styled-components';

const Wrapper = styled.section<{ activeZIndex: boolean }>`
  position: relative;
  z-index: 20;
  background: ${brand.grey[95]};
  min-height: calc(100vh - ${global.navHeight}px);

  display: flex;
  gap: 12px;

  @media ${from(Device.TabletLarge)} {
    ${defaultPadding}
    padding-top: 25px !important;
    gap: 30px;
  }

  @media ${until(Device.TabletLarge)} {
    z-index: ${({ activeZIndex }) => (activeZIndex ? '1010' : '20')}; // used to overlay nav zIndex
    flex-direction: column;
  }
`;

const Filters = styled.div`
  position: sticky;
  z-index: 20;
  top: -1px;
  margin-bottom: 32px;
  background: ${brand.grey[95]};
`;

const MobileFilterContainer = styled.div`
  display: none;

  @media ${until(Device.TabletLarge)} {
    display: block;
  }
`;

const DesktopFilterContainer = styled.div`
  display: block;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;

const FilterWrapper = styled.div<{ inView: boolean }>`
  width: 100%;
  background-color: ${brand.grey[25]};
  padding: 32px 0px;
  position: relative;
  z-index: 12;

  @media ${until(Device.TabletLarge)} {
    padding: 0;
  }
`;

const FilterTitleWrapper = styled.div<{ isActive: boolean }>`
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
  }

  ${({ isActive }) =>
    isActive
      ? css`
          border-top: 1px solid transparent;

          &:after {
            border-bottom: 1px solid ${brand.grey[25]};
          }
        `
      : css`
          border-top: 1px solid ${brand.grey[80]};

          &:after {
            border-bottom: 1px solid ${brand.grey[80]};
          }
        `}
`;

const SortButton = styled.div`
  position: relative;
  z-index: 11;
  grid-column: span 6;

  @media ${from(Device.Tablet)} {
    z-index: 21;
    grid-column-start: -1;
    grid-column-end: -1;
  }
`;

const FilterButton = styled.button<{ isActive: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  ${fonts.helvetica['73-bold-extended']}
  font-size: 16px;
  text-transform: uppercase;
  width: 100%;
  border: 1px solid transparent;
  cursor: pointer;
  border: 0;
  outline: 0;
  border-radius: 0;
  grid-column: span 6;
  padding: 16px 0px;

  @media ${from(Device.Mobile)} {
    height: 100%;
    padding: 16px 24px;
  }

  @media ${from(Device.Tablet)} {
    border-left: 1px solid ${brand.grey[80]};
    border-right: 1px solid ${brand.grey[80]};
    height: 48px;
    padding: 0px 24px;
    grid-column-start: 1;
    grid-column-end: 1;
  }

  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 12px;
  justify-content: space-between;

  > svg {
    width: 34px;
    height: 22px;
    flex-shrink: 0;

    &:last-child {
      width: 12px;
      height: 16px;
    }
  }

  ${({ isActive }) =>
    isActive
      ? css`
          background-color: ${brand.grey[25]};
          color: ${brand.white};
          border-left: 1px solid transparent;
          border-right: 1px solid transparent;

          svg:last-child {
            transform: rotate(180deg);
          }
        `
      : css`
          background-color: transparent;
          color: ${brand.black};

          /* border-left: 1px solid ${brand.grey[80]};
          border-right: 1px solid ${brand.grey[80]}; */
        `}
`;

const CloseButton = styled.button.attrs({ type: 'button' })`
  display: block;
  position: absolute;
  z-index: 10;
  top: 0;
  right: -50px;
  width: 50px;
  height: 48px;
  background: ${brand.black} url(${XWhite}) no-repeat center;
  cursor: pointer;
  border: 0;
  outline: 0;
  border-radius: 0;
  opacity: 0;
`;

const FilterTitle = styled.div`
  ${fonts.helvetica['93-black-extended']};
  font-size: 16px;
  color: ${brand.black};
  text-transform: uppercase;
  padding: 16px;

  @media ${from(Device.TabletLarge)} {
    flex: 1;
    padding: 0;
    margin-bottom: 16px;
  }
`;

const MobileFilter = styled.div<{ isOpen: boolean }>`
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  width: calc(100% - 50px);
  height: 100%;
  background: ${brand.white};
  transform: translateX(-100%);
  opacity: 0;
  pointer-events: none;
  transition: transform 300ms ${animation.easeInOutCubic}, opacity 300ms ${animation.easeInOutCubic};

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: -50px;
    width: 50px;
    height: 100%;
    background: rgb(0 0 0 / 50%);
    opacity: 0;
  }

  ${({ isOpen }) =>
    isOpen
      ? css`
          opacity: 1;
          transform: translateX(0px);
          pointer-events: all;

          &:after {
            opacity: 1;
            transition: opacity 300ms ${animation.easeInOutCubic} 200ms;
          }

          ${CloseButton} {
            opacity: 1;
            transition: opacity 300ms ${animation.easeInOutCubic} 200ms;
          }
        `
      : null};

  @media ${from(Device.TabletLarge)} {
    display: none;
  }

  ${FilterDropdownStyles.Dropdown} {
    display: block;
    height: auto;
    margin-bottom: 1px;
  }

  ${FilterDropdownStyles.Title} {
    height: 48px;
  }

  ${FilterDropdownStyles.List} {
    position: relative;
    top: 0;
    left: -16px;
    width: calc(100% + 32px);
    border: none;
    border-top: 1px solid ${brand.black};
    transition: opacity 150ms ${animation.easeInOutCubic}, transform 150ms ${animation.easeInOutCubic},
      max-height 150ms ${animation.easeInOutCubic};
  }
`;

const MobileFilterInner = styled.div`
  overflow: auto;
  height: 100%;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    width: 0 !important;
  }
`;

const ProductList = styled.div<{ singleCardOnMobile?: boolean; wideCards?: boolean; carouselMobile?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 12px;
  margin-bottom: 48px;

  --mobile-columns: 2;
  --large-mobile-columns: 2;
  --tablet-columns: 3;
  --desktop-columns: 3;

  [data-hide-filters='true'] & {
    --tablet-columns: 4;
    --desktop-columns: 4;
  }

  ${({ singleCardOnMobile }) =>
    singleCardOnMobile &&
    css`
      --mobile-columns: 1;
    `}

  ${({ wideCards }) =>
    wideCards &&
    css`
      --mobile-columns: 1;
      --large-mobile-columns: 2;
      --tablet-columns: 1;
      --desktop-columns: 2;
    `}

  @media ${from(Device.Mobile)} {
    grid-template-columns: repeat(var(--mobile-columns), 1fr);
  }

  @media ${from(Device.MobileLarge)} {
    grid-template-columns: repeat(var(--large-mobile-columns), 1fr);
  }

  @media ${from(Device.TabletLarge)} {
    grid-gap: 30px;
    grid-template-columns: repeat(var(--tablet-columns), 1fr);
  }

  @media ${from(Device.DesktopLarge)} {
    grid-template-columns: repeat(var(--desktop-columns), 1fr);
  }

  @media ${until(Device.TabletLarge)} {
    ${({ carouselMobile }) =>
      carouselMobile
        ? css`
            grid-auto-flow: column;
            grid-template-rows: auto;
            overflow: auto;
            grid-template-columns: 220px;
            grid-auto-columns: 220px;
          `
        : null}
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const LoadingWrapper = styled.div`
  min-height: 45vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Pagination = styled.div`
  grid-column: span 12;
  margin-bottom: 40px;

  > div {
    display: flex;
    flex-direction: row;
    justify-content: center;
  }

  ul {
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: center;
    flex-direction: row;
    background: #dedede;
    padding: 5px;

    .next a,
    .previous a {
      width: 40px;
      height: 26px;
      background-image: url('${ChevronUrl}');
      background-position: center;
      background-size: contain;
      background-repeat: no-repeat;
      padding: 0px;
      background-size: 10px;
    }

    .previous a {
      transform: rotate(180deg);
    }

    li {
      display: block;
      min-width: 35px;
      text-align: center;
      cursor: pointer;
      padding: 0px;

      @media ${from(Device.MobileLarge)} {
        min-width: 40px;
      }

      &:first-child {
        padding-left: 0px;
      }

      &:last-child {
        padding-right: 0px;
      }

      a {
        display: block;
        padding: 5px;
        outline: none;

        @media ${from(Device.MobileLarge)} {
          padding: 5px 15px;
        }

        &:hover {
          background-color: #f2f2f2;
        }
      }
    }

    .selected {
      ${fonts.helvetica['63-medium-extended']};
      text-decoration: underline;
      background: #f2f2f2;
    }
  }
`;

const Group = styled.div`
  padding: 40px 0px;
`;

const GroupHeading = styled.div`
  margin: 35px 0px 40px;
  text-align: center;

  h2 {
    ${Typography.H6Style};
  }
`;

const GroupFooter = styled.div`
  text-align: center;
  margin-bottom: 25px;

  h3 {
    font-size: 12px;
    line-height: 1;
    text-transform: uppercase;
    ${fonts.helvetica['73-bold-extended']};
  }
`;

const GroupButtons = styled.div`
  margin-top: 18px;

  ${ButtonStyles.ButtonElement} {
    margin-right: 10px;

    &:last-child {
      margin-right: 0px;
    }
  }
`;

const ProductListWrapper = styled(Grid.Default)`
  margin-top: 0;

  [data-hide-filters='true'] & {
    padding-top: ${fluid(15, 30)};
  }

  @media ${from(Device.TabletLarge)} {
    padding: 0 !important;
  }
`;

const CompareToggleContainer = styled.div`
  display: flex;
  align-items: center;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;

const CompareText = styled.div`
  ${Typography.PStyle};
  ${fonts.helvetica['73-bold-extended']};
  text-transform: uppercase;
  margin-left: 10px;
`;

const ToggleSlider = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: block;
  padding: 3px;
  border-radius: 30px;
  background-color: ${brand.grey[50]};
  cursor: pointer;
  transition: background-color 0.3s ${animation.easeInOutCubic};

  &::before {
    content: '';
    position: absolute;
    left: 3px;
    bottom: 2px;
    display: block;
    height: 21px;
    width: 21px;
    border-radius: 30px;
    background-color: ${brand.white};
    transition: background-color 0.3s ${animation.easeInOutCubic}, transform 0.3s ${animation.easeInOutCubic};
  }
`;

const ToggleLabel = css`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${fonts.helvetica['55-roman']};
  font-size: 12px;
  letter-spacing: 2px;
  line-height: 14px;
  text-align: center;
  text-transform: uppercase;
  transition: color 0.3s ${animation.easeInOutCubic};
  padding: 0 10px;
  width: 100%;
  max-width: 50%;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const ToggleLabelOn = styled.span`
  ${ToggleLabel};

  color: ${brand.black};
`;

const ToggleLabelOff = styled.span`
  ${ToggleLabel};

  color: ${brand.white};
`;

const ToggleSwitch = styled.label<{ disabled?: boolean; active?: boolean }>`
  position: relative;
  display: inline-flex;
  height: 25px;
  margin: 0;
  padding: 0;
  width: 50px;
  cursor: pointer;

  ${({ active }) =>
    active
      ? css`
          ${ToggleSlider} {
            background-color: ${brand.red.base};

            &::before {
              transform: translateX(100%);
            }
          }

          ${ToggleLabelOn} {
            color: ${brand.white};
          }

          ${ToggleLabelOff} {
            color: ${brand.black};
          }
        `
      : null}

  input {
    display: none;
  }
`;

const Documents = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: ${fluid(24, 40)} 0px;
  gap: 30px;
  row-gap: 12px;

  &[data-align='center'] {
    justify-content: center;
    padding-top: 0px;

    @media ${until(Device.TabletLarge)} {
      justify-content: flex-start;
    }
  }

  &[data-column='true'] {
    flex-direction: column;

    @media ${from(Device.TabletLarge)} {
      align-items: center;
    }
  }

  &:not([data-align='center']) {
    padding: ${fluid(18, 24)} 0px;

    @media ${until(Device.TabletLarge)} {
      margin-top: -12px;
      margin-bottom: 12px;
      flex-direction: column;
    }
  }
`;

const Document = styled.a`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  color: ${brand.nearlyBlack};

  span {
    font-size: 16px;

    em {
      ${fonts.helvetica['73-bold-extended']};
      letter-spacing: 0.2px;
      margin-right: 0.5px;
    }
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    align-self: flex-start;
  }
`;

export const ProductListStyles = {
  Wrapper,
  Filters,
  FilterTitle,
  SortButton,
  MobileFilterContainer,
  DesktopFilterContainer,
  FilterTitleWrapper,
  FilterWrapper,
  FilterButton,
  CloseButton,
  MobileFilter,
  MobileFilterInner,
  ProductList,
  ButtonContainer,
  LoadingWrapper,
  Pagination,
  Group,
  GroupHeading,
  GroupFooter,
  GroupButtons,
  CompareToggleContainer,
  CompareText,
  ToggleSlider,
  ToggleLabel,
  ToggleLabelOn,
  ToggleLabelOff,
  ToggleSwitch,
  ProductListWrapper,
  Documents,
  Document,
};
